import { useMutation } from '@apollo/client'
import { Ai } from '@factorialco/factorial-one-react/icons/app'
import {
  MeetingsPostSummaryDocument,
  MeetingsPostSummaryMutation,
  MeetingsPreSummaryDocument,
  MeetingsPreSummaryMutation,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import {
  POST_MEETING_TEMPLATE_TYPE,
  PRE_MEETING_TEMPLATE_TYPE,
} from 'modules/meetings/lib/constants/AIQueryTypes'
import { errorBlock } from 'modules/meetings/lib/constants/TipTapBlock'

type MeetingState = 'pre' | 'post'

interface UseBasicTextEditorConfigProps {
  meetingId?: string | number
  meetingState?: MeetingState
  setNotes: (notes: string) => void
}

export const useBasicTextEditorConfig = ({
  meetingId,
  meetingState,
  setNotes,
}: UseBasicTextEditorConfigProps) => {
  const [postSummaryFormattedContent] = useMutation<MeetingsPostSummaryMutation>(
    MeetingsPostSummaryDocument
  )

  const [preSummaryFormattedContent] = useMutation<MeetingsPreSummaryMutation>(
    MeetingsPreSummaryDocument
  )

  const handleGeneratePreSummary = async (agendaType: string) => {
    if (!meetingId) return null

    try {
      const { data } = await preSummaryFormattedContent({
        variables: {
          meetingId: Number(meetingId),
          agendaType,
        },
      })

      if (data?.meetingsMutations?.generateMeetingsPreSummary?.preSummary?.formattedContent) {
        const formattedContent =
          data.meetingsMutations.generateMeetingsPreSummary.preSummary.formattedContent
        setNotes(formattedContent)
        try {
          const parsedContent = JSON.parse(formattedContent)
          return parsedContent
        } catch (error) {
          return errorBlock(i18n.t('meetings.notes.labels.errorBlock.text'))
        }
      }
    } catch (error) {
      return errorBlock(i18n.t('meetings.notes.labels.errorBlock.text'))
    }
    return null
  }

  const handleGeneratePostSummary = async (templateType: string) => {
    if (!meetingId) return null

    try {
      const { data } = await postSummaryFormattedContent({
        variables: {
          meetingId: Number(meetingId),
          templateType,
        },
      })

      if (data?.meetingsMutations?.generateMeetingsPostSummary?.postSummary?.formattedContent) {
        const formattedContent =
          data.meetingsMutations.generateMeetingsPostSummary.postSummary.formattedContent
        setNotes(formattedContent)
        try {
          const parsedContent = JSON.parse(formattedContent)
          return parsedContent
        } catch (error) {
          return errorBlock(i18n.t('meetings.notes.labels.errorBlock.text'))
        }
      }
    } catch (error) {
      return errorBlock(i18n.t('meetings.notes.labels.errorBlock.text'))
    }
    return null
  }

  const toolbarLabels = {
    bold: i18n.t('rich_text_editor.toolbar.bold'),
    italic: i18n.t('rich_text_editor.toolbar.italic'),
    underline: i18n.t('rich_text_editor.toolbar.underline'),
    strike: i18n.t('rich_text_editor.toolbar.strike'),
    highlight: i18n.t('rich_text_editor.toolbar.highlight'),
    heading1: i18n.t('rich_text_editor.toolbar.heading1'),
    heading2: i18n.t('rich_text_editor.toolbar.heading2'),
    heading3: i18n.t('rich_text_editor.toolbar.heading3'),
    left: i18n.t('rich_text_editor.toolbar.left'),
    center: i18n.t('rich_text_editor.toolbar.center'),
    right: i18n.t('rich_text_editor.toolbar.right'),
    justify: i18n.t('rich_text_editor.toolbar.justify'),
    bulletList: i18n.t('rich_text_editor.toolbar.bulletList'),
    orderedList: i18n.t('rich_text_editor.toolbar.orderedList'),
    taskList: i18n.t('rich_text_editor.toolbar.taskList'),
    codeBlock: i18n.t('rich_text_editor.toolbar.codeBlock'),
    horizontalRule: i18n.t('rich_text_editor.toolbar.horizontalRule'),
    quote: i18n.t('rich_text_editor.toolbar.quote'),
    moreOptions: i18n.t('rich_text_editor.toolbar.moreOptions'),
    code: i18n.t('rich_text_editor.toolbar.code'),
    divider: i18n.t('rich_text_editor.toolbar.divider'),
    bullet: i18n.t('rich_text_editor.toolbar.bullet'),
    ordered: i18n.t('rich_text_editor.toolbar.ordered'),
    task: i18n.t('rich_text_editor.toolbar.task'),
    linkPlaceholder: i18n.t('rich_text_editor.toolbar.linkPlaceholder'),
    linkLabel: i18n.t('rich_text_editor.toolbar.linkLabel'),
    linkPaste: i18n.t('rich_text_editor.toolbar.linkPaste'),
    close: i18n.t('rich_text_editor.toolbar.close'),
    details: i18n.t('rich_text_editor.toolbar.dropdown'),
  }

  const postMeetingButtons = [
    {
      type: POST_MEETING_TEMPLATE_TYPE.FACTORIAL_FORMAT,
      emoji: '🤖',
      label: i18n.t('meetings.meeting.post_meeting_buttons.one_on_one_summary'),
      icon: Ai,
    },
    {
      type: POST_MEETING_TEMPLATE_TYPE.TALK_AGREE_ACTION,
      emoji: '⏱️',
      label: i18n.t('meetings.meeting.post_meeting_buttons.agreements_and_next_steps'),
      icon: Ai,
    },
    {
      type: POST_MEETING_TEMPLATE_TYPE.DECISIONS_BLOCKERS_NEXT,
      emoji: '📊',
      label: i18n.t('meetings.meeting.post_meeting_buttons.decisions_and_blockers'),
      icon: Ai,
    },
    {
      type: POST_MEETING_TEMPLATE_TYPE.WINS_CHALLENGES_FOCUS,
      emoji: '🎯',
      label: i18n.t('meetings.meeting.post_meeting_buttons.wins_and_challenges'),
      icon: Ai,
    },
  ]

  const preMeetingButtons = [
    {
      type: PRE_MEETING_TEMPLATE_TYPE.FACTORIAL_FORMAT,
      emoji: '🤖',
      label: i18n.t('meetings.meeting.pre_meeting_buttons.last_meeting_recap'),
      icon: Ai,
    },
    {
      type: PRE_MEETING_TEMPLATE_TYPE.FOLLOW_UP_ACCOUNTABILITY,
      emoji: '⏱️',
      label: i18n.t('meetings.meeting.pre_meeting_buttons.follow_up'),
      icon: Ai,
    },
    {
      type: PRE_MEETING_TEMPLATE_TYPE.STRATEGY_PRIORITIES,
      emoji: '⭐️',
      label: i18n.t('meetings.meeting.pre_meeting_buttons.strategy_and_priorities'),
      icon: Ai,
    },
    {
      type: PRE_MEETING_TEMPLATE_TYPE.GOAL_PROGRESS_GROWTH,
      emoji: '📊',
      label: i18n.t('meetings.meeting.pre_meeting_buttons.goals_and_progress'),
      icon: Ai,
    },
    {
      type: PRE_MEETING_TEMPLATE_TYPE.PERFORMANCE_FEEDBACK,
      emoji: '🎯',
      label: i18n.t('meetings.meeting.pre_meeting_buttons.last_performance_review'),
      icon: Ai,
    },
  ]

  const moodTrackerLabels = {
    deleteBlock: i18n.t('meetings.notes.labels.moodTrackerLabels.deleteBlock'),
    expand: i18n.t('meetings.notes.labels.moodTrackerLabels.expand'),
    collapse: i18n.t('meetings.notes.labels.moodTrackerLabels.collapse'),
  }

  const aiBlockLabels = {
    reset: i18n.t('meetings.notes.labels.aiBlockLabels.reset'),
    resetDescription: i18n.t('meetings.notes.labels.aiBlockLabels.resetDescription'),
    deleteBlock: i18n.t('meetings.notes.labels.aiBlockLabels.deleteBlock'),
    expand: i18n.t('meetings.notes.labels.aiBlockLabels.expand'),
    collapse: i18n.t('meetings.notes.labels.aiBlockLabels.collapse'),
  }

  const slashCommandGroupLabels = {
    textStyles: i18n.t('meetings.notes.labels.slashCommandGroupLabels.textStyles'),
    lists: i18n.t('meetings.notes.labels.slashCommandGroupLabels.lists'),
    blocks: i18n.t('meetings.notes.labels.slashCommandGroupLabels.blocks'),
  }

  const transcriptLabels = {
    deleteBlock: i18n.t('meetings.notes.labels.transcriptLabels.deleteBlock'),
    expand: i18n.t('meetings.notes.labels.transcriptLabels.expand'),
    collapse: i18n.t('meetings.notes.labels.transcriptLabels.collapse'),
    messagesCount: i18n.t('meetings.notes.labels.transcriptLabels.messagesCount'),
    messagesCountSingular: i18n.t('meetings.notes.labels.transcriptLabels.messagesCountSingular'),
  }

  const liveCompanionLabels = {
    deleteBlock: i18n.t('meetings.notes.labels.liveCompanionLabels.deleteBlock'),
    expand: i18n.t('meetings.notes.labels.liveCompanionLabels.expand'),
    collapse: i18n.t('meetings.notes.labels.liveCompanionLabels.collapse'),
    oneTopicWithCommentary: i18n.t(
      'meetings.notes.labels.liveCompanionLabels.oneTopicWithCommentary'
    ),
    multipleTopicsWithCommentary: i18n.t(
      'meetings.notes.labels.liveCompanionLabels.multipleTopicsWithCommentary'
    ),
  }

  const chatLabels = {
    deleteBlock: i18n.t('meetings.notes.labels.chatLabels.deleteBlock'),
    expand: i18n.t('meetings.notes.labels.chatLabels.expand'),
    collapse: i18n.t('meetings.notes.labels.chatLabels.collapse'),
    messagesCount: i18n.t('meetings.notes.labels.chatLabels.messagesCount'),
    messagesCountSingular: 'message',
  }

  const labels = {
    toolbarLabels,
    slashCommandGroupLabels,
    moodTrackerLabels,
    aiBlockLabels,
    transcriptLabels,
    liveCompanionLabels,
    chatLabels,
  }

  const getAiBlockConfig = () => {
    if (!meetingState) return undefined

    return {
      title:
        meetingState === 'post'
          ? i18n.t('meetings.meeting.post_meeting_buttons.title')
          : i18n.t('meetings.meeting.pre_meeting_buttons.title'),
      onClick: async (type: string) => {
        try {
          const content =
            meetingState === 'post'
              ? await handleGeneratePostSummary(type)
              : await handleGeneratePreSummary(type)

          if (content) {
            return content
          } else {
            return errorBlock(i18n.t('meetings.notes.labels.errorBlock.text'))
          }
        } catch (error) {
          return errorBlock(i18n.t('meetings.notes.labels.errorBlock.text'))
        }
      },
      buttons: meetingState === 'post' ? postMeetingButtons : preMeetingButtons,
    }
  }

  return {
    labels,
    aiBlockConfig: getAiBlockConfig(),
  }
}
